import type { GeneratedQuestion } from '~/components/AIEnhance/Constants';
import $axios from '../AxiosService';

interface AIService {
  getSubsections: (
    { content, numberOfSections, meta, lang }: { content: string; numberOfSections: number; meta?: Record<string, any>; lang: string }
  ) => Promise<IApiResponse>,
  getSlidesForText: (
    { topic, slideType, meta, subject, lang }: { topic: string; slideType: 'INTRODUCTION' | 'INFORMATION' | 'TRIVIA' | 'EXPLANATION'; subject?: string; meta?: Record<string, any>; lang?: string }
  ) => Promise<IApiResponse>,
  getExplanationForQuestion: (
    { question, meta, subject }: { question: any; subject?: string; meta?: Record<string, any> }
  ) => Promise<IApiResponse>,
  generateQuestionsFromText: (
    { title, data, meta, lang }: { title: string; data: { text: string; type: string }[]; meta?: Record<string, any>; lang?: string }
  ) => Promise<IApiResponse>,
  getNextQuestionSuggestion: (
    quizId: string,
    args: { lastQuestion?: GeneratedQuestion; currentIndex?: number; title?: string }
  ) => Promise<IApiResponse>,
}

const QuizizzAIService: AIService = {
  async getSubsections({ content, numberOfSections, meta, lang }) {
    try {
      const response = await $axios().post('/quiz-generator/generate-subsections-from-topic', {
        content,
        numberOfSections,
        meta,
        params: {
          language: lang,
        },
      });
      return {
        success: true,
        data: response.data.data.response.lesson,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  },

  async getSlidesForText({ topic, slideType, meta, lang = 'en' }) {
    try {
      const response = await $axios().post('/quiz-generator/generate-slides-from-topic', {
        content: topic,
        slideType,
        meta,
        params: {
          language: lang,
        },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  },

  async getExplanationForQuestion({ question, meta }) {
    try {
      const response = await $axios().post('/quiz-generator/generate-slides-from-topic', {
        content: question,
        slideType: 'EXPLANATION',
        meta,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  },

  async generateQuestionsFromText(data) {
    try {
      const payload = {
        title: data.title,
        data: data.data,
        meta: data.meta,
        params: {
          language: data.lang,
        },
      };
      const response = await $axios().post('/quiz-generator/text/questions', payload);
      const result = response.data;
      return {
        success: true,
        data: result.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response,
        data: null,
      };
    }
  },

  async getNextQuestionSuggestion(quizId, payload) {
    try {
      const { data: response } = await $axios().post(`/quiz/suggest-next-question/${quizId}`, payload);

      const { success, data } = response;
      if (success) {
        return {
          success,
          data,
        };
      }
      return {
        success: false,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  },
};

export default QuizizzAIService;
